<script lang="ts">
  import '@app/styles/app.css';
  import '@app/styles/fonts.css';

  import {
    PUBLIC_DO_ASSETS_URL,
    PUBLIC_NODE_URL,
    PUBLIC_WALLETCONNECT_PROJECT_ID,
    PUBLIC_USDC_TOKEN_ADDRESS,
    PUBLIC_SUPPORTED_WALLET_CHAINS,
  } from '$env/static/public';

  import { browser } from '$app/environment';
  import { page } from '$app/stores';
  import { onMount } from 'svelte';

  import { initDirectusImages } from '@do/svelte-directus-images';
  import { Seo } from '@do/svelte-seo';
  import { SiteStatus } from '@do/models';

  import { LottieCDN } from '@shared/lib/lotties/scripts';
  import * as themeModel from '@shared/ui/theme/model';
  import { getFromLocalStorage } from '@shared/lib/tools';
  import { mobileMenuActivity } from '@shared/lib';
  import { TransitionAnimator } from '@shared/ui/transition-animator';
  import { overlayActivity } from '@shared/ui/overlay/model';
  import { defaultMetaDescription, defaultTitle } from '@shared/config';
  import type { HashString } from '@shared/types';
  import { Breadcrumbs } from '@shared/ui/breadcrumbs';
  import { Overlay } from '@shared/ui/overlay';

  import { userModel } from '@entities/user';

  import { Snackbar } from '@features/snackbar';

  import { Layout } from '@widgets/layout';
  import { WalletSidebar } from '@widgets/wallet-sidebar';
  import { Footer } from '@widgets/footer';
  import { MobileMenu } from '@widgets/mobile-menu';
  import { PlaceholderLayout } from '@widgets/placeholder-layout';

  import type { LayoutData } from './$types';
  import { UnleashProxyClient } from '@shared/lib/unleash';
  import { Web3Manager } from '@do/svelte-web3-manager';
  import { purchaseModel } from '@entities/purchase';
  import { saleModel } from '@entities/sale';

  const unleashClient = new UnleashProxyClient();
  unleashClient.init();

  export let data: LayoutData;

  let mounted = false;

  $: siteName = `${$page.data.configurations.name} - Digital Originals Store`;

  initDirectusImages({
    transformationsPreset: data.transformationPreset,
    baseUrl: PUBLIC_DO_ASSETS_URL,
    defaultPlaceholder: {
      src: '/images/artwork-placeholder.png',
      width: 1000,
      height: 1000,
    },
  });

  $: isOpened($mobileMenuActivity || $overlayActivity, mounted);

  function isOpened(open: boolean, mounted: boolean) {
    if (mounted) document.body.style.overflow = open ? 'hidden' : '';
  }

  const web3Manager = new Web3Manager();

  web3Manager.init({
    metadata: data.metadata,
    projectId: PUBLIC_WALLETCONNECT_PROJECT_ID,
    chains: PUBLIC_SUPPORTED_WALLET_CHAINS.split(','),
    node: PUBLIC_NODE_URL,
    token: PUBLIC_USDC_TOKEN_ADDRESS as HashString,
  });

  purchaseModel.init();
  saleModel.init();

  onMount(() => {
    mounted = true;
    themeModel.theme.useLocalStorage();
    userModel.notifications.useLocalStorage();

    if (browser) {
      const json = getFromLocalStorage(themeModel.themeKey);

      if (json) {
        const { mode } = json;
        document.documentElement.classList.add(mode);
      }
    }
  });
</script>

<LottieCDN />

<Seo
  {siteName}
  {defaultTitle}
  {defaultMetaDescription}
  siteUrl="{$page.url.origin}/"
  settings="{{ openGraph: true, twitter: false, canonical: true, schemaOrg: false }}"
  pageSeoData="{$page.data.seo}"
  openGraph="{data.configurations.open_graph}"
  faviconHtml="{data.configurations.favicon_html}"
/>

<Snackbar />
<Overlay />

{#if data.configurations.status === SiteStatus.PreRelease}
  <PlaceholderLayout>
    <slot />
  </PlaceholderLayout>
{:else}
  <Layout>
    <Breadcrumbs />
    <slot />
    <WalletSidebar />
    <Footer />
    <MobileMenu />
    <TransitionAnimator />
  </Layout>
{/if}
